import useIsMounted from "../../../../../hooks/use-is-mounted";
import React, {useCallback, useEffect, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import axios from "axios";
import {Box, DialogActions, DialogContent, Divider, LinearProgress, Tooltip, Typography} from "@mui/material";
import ImportingLottieAnimation from "../../../../app-specific/lottie-animations/importing";
import Button from "../../../../base/button/button";
import {ProductTypes} from "../../../../../../core/constants/enums";
import ToastService from "../../../../../../core/services/toast";


const LoadingSection = ({selectedItems, customerId, invoiceNo, onPreviousStep, onNextStep}) => {
    const isMounted = useIsMounted();
    const [status, setStatus] = useState({
        message: 'Processing your request...',
        loading: true,
        progress: 0,
    })

    /**
     * Issues the credit for the selected items.
     *
     * - Creates a quote from the selected items with negative quantities.
     * - Updates the quote with the unit prices.
     * - Creates an order from the quote.
     * - Commits the quantities and serials of the order.
     * - Sets the reference number of the order.
     *
     * @param cancelToken {CancelToken}
     * @type {(function(*): Promise<undefined|*>)}
     */
    const issueCredit = useCallback(async (cancelToken) => {
        setStatus({
            message: 'Creating Quote...',
            loading: true,
            progress: 12.5,
        })
        // Create Quote from provided items but with negative quantities
        const createQuoteRequest = {
            customerId: customerId,
            parts: selectedItems.map(e => ({
                partNumber: e.partNo,
                qty: -e.committedQuantity,
                warehouseCode: e.warehouseCode,
            }))
        }
        const createQuoteResponse = await Api.createNewQuoteV2(createQuoteRequest, false, cancelToken);
        if (!isMounted())
            return;
        if (!createQuoteResponse?.resultFlag) {
            ToastService.error({
                message: createQuoteResponse?.message ?? 'An error occurred while creating the quote. Going back to the previous step.',
                title: 'Quote Creation Error',
                hideTicketSubmission: true,
            })
            return onPreviousStep();
        }
        const quoteId = createQuoteResponse.data.internalId;
        let orderNo = createQuoteResponse.data.orderNumber;
        // Update quote.
        setStatus({
            message: 'Updating Quote Items...',
            loading: true,
            progress: 25,
        })
        const updateQuoteRequest = {
            customerId: customerId,
            orderId: quoteId,
            PartCountChangedList: selectedItems.map(e => ({
                partNo: e.partNo,
                finalCount: -e.committedQuantity,
                unitPrice: e.unitPrice,
                whs: e.warehouseCode,
                partDescription: null,
            })),
        }
        const updateQuoteResponse = await Api.updateQuoteItems(updateQuoteRequest, cancelToken);
        if (!isMounted())
            return;
        if (!updateQuoteResponse?.resultFlag) {
            ToastService.error({
                message: updateQuoteResponse?.message ?? 'An error occurred while updating the quote. Going back to the previous step.',
                title: 'Quote Update Error',
                hideTicketSubmission: true,
            })
            // Remove Quote
            await Api.removeQuoteByCustomer(customerId, quoteId, cancelToken);
            if (!isMounted())
                return;
            return onPreviousStep();
        }
        // Create Order from the created quote
        setStatus({
            message: 'Creating The Order...',
            loading: true,
            progress: 37.5,
        })
        const createOrderRequest = {
            customerId: customerId,
            orderId: quoteId,
            userToBackOrderPermission: false,
            note: "",
        }
        const createOrderResponse = await Api.generateSalesOrder(createOrderRequest, cancelToken);
        if (!isMounted())
            return;
        if (!createOrderResponse?.resultFlag) {
            ToastService.error({
                message: createOrderResponse?.message ?? 'An error occurred while creating the sales order. Going back to the previous step.',
                title: 'Sales Order Creation Error',
                hideTicketSubmission: true,
            })
            // Remove Quote
            await Api.removeQuoteByCustomer(customerId, quoteId, cancelToken);
            if (!isMounted())
                return;
            return onPreviousStep();
        }
        const salesOrderId = quoteId;
        orderNo = createOrderResponse.data?.orderNumber ?? orderNo;
        // Commit the order serials and quantities.
        setStatus({
            message: 'Committing The Order Serials / Quantities...',
            loading: true,
            progress: 50,
        })
        const itemsReport = [];
        const itemsToBeCommitted = selectedItems.filter(e => !!e.typeInfo?.code && e.typeInfo?.code !== ProductTypes.nonPhysical);
        const itemsLength = itemsToBeCommitted.length;

        const nonCommittedItems = selectedItems.filter(e => !itemsToBeCommitted.includes(e));
        for (const nonCommittedItem of nonCommittedItems) {
            itemsReport.push({
                message: 'Success',
                resultFlag: true,
                item: nonCommittedItem,
            });
        }

        const unSerializedItems = itemsToBeCommitted.filter(e => !e.isSerialized);
        for (const unSerializedItem of unSerializedItems) {
            setStatus(prevState => ({
                ...prevState,
                message: `Committing The Quantities for ${unSerializedItem.partNo}...`,
            }));
            const updateUnSerializedItemRequest = {
                customerId: customerId,
                saleOrderId: salesOrderId,
                partNumber: unSerializedItem.partNo,
                commitQuantity: -unSerializedItem.committedQuantity,
                backOrderQuantity: 0,
            }
            const res = await Api.updateSalesOrderCommittedQuantityOfPart(updateUnSerializedItemRequest, cancelToken);
            if (!isMounted())
                return;
            itemsReport.push({
                message: res?.message ?? (!res?.resultFlag ? 'An error occurred while committing the quantities.' : "Success"),
                resultFlag: res?.resultFlag ?? false,
                item: unSerializedItem,
            });
            setStatus(prevState => ({
                ...prevState,
                progress: prevState.progress + (40 / itemsLength),
            }));
        }
        const serializedItems = itemsToBeCommitted.filter(e => !!e.isSerialized);
        for (const serializedItem of serializedItems) {
            setStatus(prevState => ({
                ...prevState,
                message: `Committing The Serials for ${serializedItem.partNo}...`,
            }));
            const updateSerialNumbersRequest = {
                customerId: customerId,
                saleOrderId: salesOrderId,
                partNumber: serializedItem.partNo,
                serialNumbers: serializedItem.serials?.map(serial => ({
                    id: null,
                    serialNumber: serial,
                    isAvailable: true,
                })) ?? [],
                backOrderQuantity: 0,
            }
            const res = await Api.updateSalesOrderPartSerialNumbers(updateSerialNumbersRequest, cancelToken);
            if (!isMounted())
                return;
            itemsReport.push({
                message: res?.message ?? (!res?.resultFlag ? 'An error occurred while committing the serials.' : "Success"),
                resultFlag: res?.resultFlag ?? false,
                item: serializedItem,
            });
            setStatus(prevState => ({
                ...prevState,
                progress: prevState.progress + (40 / itemsLength),
            }));
        }

        // Set the PO and reference number of the order to the source invoice.
        setStatus({
            message: `Setting The Reference Number of Order...`,
            loading: true,
            progress: 95,
        });
        const salesOrderResponse = await Api.getSalesOrderInformationForCustomer(customerId, salesOrderId, false);
        if (!isMounted())
            return;
        const salesOrder = salesOrderResponse?.resultFlag ? salesOrderResponse.data : null;
        const settingReferenceNoRequest = {
            customerId: customerId,
            orderId: salesOrderId,
            customerPo: invoiceNo,
            fob: salesOrder?.fob ?? '',
            requiredDate: salesOrder?.requiredDate ?? null,
            referenceNo: invoiceNo,
            territoryCode: salesOrder?.territory?.code ?? salesOrder?.territory ?? '',
            accountManagerCode: salesOrder?.accountManager?.code ?? salesOrder?.accountManagerNo ?? null,
            paymentTerms: salesOrder?.termsCode ?? salesOrder?.paymentTermsCode ?? '',
        };
        const settingReferenceNoResponse = await Api.updateSalesOrdersPOAndFob(settingReferenceNoRequest, cancelToken, false);
        if (!isMounted())
            return;
        setStatus({
            message: `Order Issued, Wrapping Up...`,
            loading: true,
            progress: 100,
        });
        setTimeout(() => {
            if (!isMounted())
                return;
            onNextStep({
                orderId: salesOrderId,
                orderNo: orderNo,
                customerId: customerId,
                itemsReport: itemsReport,
                referenceSet: settingReferenceNoResponse?.resultFlag,
            });
        }, 1000);
    }, [customerId, invoiceNo, isMounted, onNextStep, onPreviousStep, selectedItems])

    /**
     * Calls the issue credit function as soon as the component mounts.
     * - This is to simulate the process of issuing credit.
     * - As soon as the component unmounts, the axios cancel token is called to cancel the request.
     */
    useEffect(() => {
        const axiosCancelToken = axios.CancelToken.source();
        void issueCredit(axiosCancelToken.token);
        return () => axiosCancelToken.cancel();
    }, [issueCredit])

    return (
        <>
            <DialogContent className={'mt--4 pb-3 px-3'}>
                {
                    status.loading &&
                    <>
                        <ImportingLottieAnimation width={'80%'}/>
                        <Tooltip
                            open={!!status.message}
                            placement={'top'}
                            arrow
                            title={status.message}
                        >
                            <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                <LinearProgress
                                    className={'w-80'}
                                    variant={'buffer'}
                                    color={'secondary'}
                                    value={status.progress}
                                    valueBuffer={100}
                                />
                                <p className={'identifier'}>{`${Math.trunc(status.progress)}%`}</p>
                            </div>
                        </Tooltip>
                        <Typography variant={'body2'} margin={3}>
                            We are issuing credit for the selected items. This may take a few moments. Please do not close this dialog nor refresh the page.
                        </Typography>
                    </>
                }
            </DialogContent>
            {
                !status.loading &&
                <DialogActions className={'p-0 d-flex flex-column'}>
                    <Divider flexItem variant={'fullWidth'}/>
                    <Box sx={{marginLeft: '0 !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => onPreviousStep()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                    </Box>
                </DialogActions>
            }
        </>
    );
}


export default LoadingSection;